import React, { useMemo } from 'react';
import {
  Button,
  Col,
  Dropdown,
  HasAccess,
  IconWrapper,
  MultiLineEllipsisText,
  PriorityBadge,
  Row,
  Space,
  Tag,
  Typography,
} from 'components';
import styles from './styles.module.scss';
import { useDetails } from './useDetails';
import {
  ConversionRateIcon,
  DollarSignIcon,
  EditIcon,
  TotalCommercialOffersIcon,
  TotalDocumentsIcon,
} from 'components/common/icons';
import { PurchaseRequestStatusBadge } from 'components/common/StatusBadge';
import { AllowedStatusTransitions, PurchaseRequestStatusType } from 'models/PurchaseRequest';
import { PERMISSIONS } from 'constants/permissions';
import { DownOutlined } from '@ant-design/icons';
import { Divider } from 'antd';
import { AssignmentEditModal } from 'pages/admin/purchaseRequest/purchaseRequest/details/assignment/edit';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';

const { Text, Title } = Typography;
export const Details = React.memo(() => {
  const {
    purchaseRequest,
    assignments,
    handleStatusChange,
    assignmentModalState,
    onAssignmentCancel,
    onAssignmentOpen,
    isFinalStatus,
  } = useDetails();

  const changeStatusMenu = useMemo(() => {
    const currentStatus = purchaseRequest.status as PurchaseRequestStatusType;
    if (!currentStatus) return [];
    const availableOptions = AllowedStatusTransitions[currentStatus] || [];
    return availableOptions.map((status) => ({
      label: status,
      key: status,
    }));
  }, [purchaseRequest.status]);
  return (
    <>
      <Row gutter={[0, 20]}>
        <Col span={24}>
          <Row gutter={[0, 8]}>
            <Col span={24}>
              <Row wrap={false} align={'middle'} className={'gap-4'}>
                <Col flex="0 1 auto">
                  <Title level={4} ellipsis={{ tooltip: true }} className={'font-weight-800 reset-title'}>
                    {purchaseRequest.uuid}
                  </Title>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={8} align={'middle'}>
                <Col>
                  <PurchaseRequestStatusBadge type={purchaseRequest.status || 'NEW'} />
                </Col>
                <Col>
                  <HasAccess role={PERMISSIONS.purchaseRequests.purchaseRequest.write}>
                    <Dropdown
                      menu={{
                        items: changeStatusMenu.map((item) => ({
                          label: <PurchaseRequestStatusBadge type={item.key as PurchaseRequestStatusType} />,
                          key: item.key,
                        })),
                        onClick: ({ key }) => handleStatusChange(key as PurchaseRequestStatusType),
                      }}
                      trigger={['click']}
                    >
                      <Button disabled={isFinalStatus} type={'primary'}>
                        <Space>
                          Change Status
                          <DownOutlined />
                        </Space>
                      </Button>
                    </Dropdown>
                  </HasAccess>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row style={{ gap: '9px' }}>
                <Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {purchaseRequest.formattedCreatedDate}
                </Text>
                <Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {purchaseRequest.formattedLastModifiedDate}
                </Text>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={16}>
                <Col>
                  <Link
                    target={'_blank'}
                    to={`${ADMIN_ROUTES.deals.view}/${purchaseRequest.dealId}/purchase-request`}
                    className={'underline-button'}
                  >
                    <Text
                      underline
                      className={'font-weight-500'}
                      style={{ color: '#05796B' }}
                      ellipsis={{ tooltip: true }}
                    >
                      Parent Deal
                    </Text>
                  </Link>
                </Col>
                <Col>
                  <Link
                    target={'_blank'}
                    to={`${ADMIN_ROUTES.leads.view}/${purchaseRequest.leadId}/general`}
                    className={'underline-button'}
                  >
                    <Text
                      underline
                      className={'font-weight-500'}
                      style={{ color: '#05796B' }}
                      ellipsis={{ tooltip: true }}
                    >
                      Original Lead
                    </Text>
                  </Link>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Divider className={styles.divider} />
        <Col span={24}>
          <Row gutter={[0, 8]}>
            <Col span={24}>
              <Row align={'middle'} justify={'space-between'}>
                <Col>
                  <Text className={'font-weight-600 font-size-16'}>Assignments</Text>
                </Col>
                <Col>
                  {!isFinalStatus && (
                    <Button icon={<EditIcon />} type="text" size={'small'} onClick={onAssignmentOpen} />
                  )}
                </Col>
              </Row>
            </Col>
            <Col>
              <Row>
                <Col span={24}>
                  <Row wrap={false} className={'gap-6'}>
                    <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                      Purchase Manager:
                    </Text>
                    <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                      {assignments.purchaseManager
                        ? ` ${assignments.purchaseManager.firstName} ${assignments.purchaseManager.lastName}`
                        : ' Not Set'}
                    </Text>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row wrap={false} className={'gap-6'}>
                    <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                      Purchase Manager Assistant:
                    </Text>
                    <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                      {assignments.purchaseManagerAssistant
                        ? ` ${assignments.purchaseManagerAssistant.firstName} ${assignments.purchaseManagerAssistant.lastName}`
                        : ' Not Set'}
                    </Text>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row wrap={false} className={'gap-6'}>
                    <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                      Sales Manager:
                    </Text>
                    <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                      {assignments.salesManager
                        ? ` ${assignments.salesManager.firstName} ${assignments.salesManager.lastName}`
                        : ' Not Set'}
                    </Text>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row wrap={false} className={'gap-6'}>
                    <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                      Sales Manager Assistant:
                    </Text>
                    <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                      {assignments.salesManagerAssistant
                        ? ` ${assignments.salesManagerAssistant.firstName} ${assignments.salesManagerAssistant.lastName}`
                        : ' Not Set'}
                    </Text>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Divider className={styles.divider} />
        <Col span={24}>
          <Row gutter={[0, 6]}>
            <Col span={24} className={'margin-b-6'}>
              <Typography.Text className={'font-weight-600 font-size-16'} style={{ color: '#05796B' }}>
                Brand Information
              </Typography.Text>
            </Col>
            <Col span={24}>
              <Link
                target={'_blank'}
                to={`${ADMIN_ROUTES.brand.view}/${purchaseRequest.brand?.id}/management`}
                className={'underline-button'}
              >
                <Text underline className={'font-weight-600 font-size-16'} ellipsis={{ tooltip: true }}>
                  {purchaseRequest.brand?.name}
                </Text>
              </Link>
            </Col>
            <Col span={24}>
              <Row align={'middle'} justify={'space-between'} wrap={false} gutter={4}>
                <Col>
                  <Row align={'middle'} className={'gap-4'} wrap={false}>
                    <Col>
                      <TotalDocumentsIcon />
                    </Col>
                    <Col>
                      <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                        Total Requests
                      </Text>
                    </Col>
                  </Row>
                </Col>
                <Col flex="0 1 auto">
                  <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }} ellipsis={{ tooltip: true }}>
                    {purchaseRequest.brand?.totalRequests || 'Not Available'}
                  </Text>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row align={'middle'} justify={'space-between'} wrap={false} gutter={4}>
                <Col>
                  <Row align={'middle'} className={'gap-4'} wrap={false}>
                    <Col>
                      <TotalCommercialOffersIcon />
                    </Col>
                    <Col>
                      <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                        Total Commercial Offers Sent
                      </Text>
                    </Col>
                  </Row>
                </Col>
                <Col flex="0 1 auto">
                  <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }} ellipsis={{ tooltip: true }}>
                    {purchaseRequest.brand?.totalCommercialOffers || 'Not Available'}
                  </Text>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row align={'middle'} justify={'space-between'} wrap={false} gutter={4}>
                <Col>
                  <Row align={'middle'} className={'gap-4'} wrap={false}>
                    <Col>
                      <ConversionRateIcon />
                    </Col>
                    <Col>
                      <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                        Conversion Rate
                      </Text>
                    </Col>
                  </Row>
                </Col>
                <Col flex="0 1 auto">
                  <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }} ellipsis={{ tooltip: true }}>
                    {purchaseRequest.brand?.conversionRate
                      ? `${purchaseRequest.brand?.conversionRate}%`
                      : 'Not Available'}
                  </Text>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={6}>
                {purchaseRequest.brand?.manualTags?.map((_tag: string, index: number) => (
                  <Col key={index + _tag}>
                    <Tag title={_tag} className={'custom-tag'}>
                      {_tag}
                    </Tag>
                  </Col>
                ))}
              </Row>
            </Col>
            <Col span={24}>
              <MultiLineEllipsisText
                maxLines={3}
                subTitle={'Additional Notes:'}
                text={purchaseRequest?.brand?.additionalNotes}
              />
            </Col>
          </Row>
        </Col>
        <Divider className={styles.divider} />
        <Col>
          <Row gutter={[0, 12]}>
            <Col span={24}>
              <Typography.Text className={'font-weight-600 font-size-16'} style={{ color: '#05796B' }}>
                Client Information
              </Typography.Text>
            </Col>
            <Col span={24}>
              <Row gutter={[0, 6]}>
                <Col span={24}>
                  <Row gutter={4} align={'middle'} wrap={false}>
                    <Col flex="0 1 auto">
                      <Link
                        target={'_blank'}
                        to={`${ADMIN_ROUTES.client}/${purchaseRequest.client?.id}/general`}
                        className={'underline-button'}
                      >
                        <Text underline className={'font-weight-600 font-size-16'} ellipsis={{ tooltip: true }}>
                          {purchaseRequest.client?.name}
                        </Text>
                      </Link>
                    </Col>
                    <Col>
                      <Text className={styles.option_tag} ellipsis={{ tooltip: true }}>
                        {purchaseRequest.client?.clientType}
                      </Text>
                    </Col>
                  </Row>
                </Col>
                <Col span={24}>
                  <PriorityBadge
                    type={purchaseRequest.client?.clientPriority}
                    tooltip={purchaseRequest.client?.priorityRationale}
                  />
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[0, 6]}>
                <Col span={24}>
                  <Row align={'middle'} gutter={4} wrap={false}>
                    {purchaseRequest.client?.countryResponse?.fileLocation && (
                      <Col>
                        <IconWrapper src={purchaseRequest.client.countryResponse.fileLocation} />
                      </Col>
                    )}
                    <Col flex="0 1 auto">
                      <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                        {purchaseRequest.client?.countryResponse?.name}
                      </Text>
                    </Col>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row align={'middle'} justify={'space-between'} wrap={false} gutter={4}>
                    <Col>
                      <Row align={'middle'} className={'gap-4'} wrap={false}>
                        <Col>
                          <TotalDocumentsIcon />
                        </Col>
                        <Col>
                          <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                            Total Inquiries
                          </Text>
                        </Col>
                      </Row>
                    </Col>
                    <Col flex="0 1 auto">
                      <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }} ellipsis={{ tooltip: true }}>
                        {purchaseRequest.client?.totalInquiries || 'Not Available'}
                      </Text>
                    </Col>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row align={'middle'} justify={'space-between'} wrap={false} gutter={4}>
                    <Col>
                      <Row align={'middle'} className={'gap-4'} wrap={false}>
                        <Col>
                          <ConversionRateIcon />
                        </Col>
                        <Col>
                          <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                            Conversion Rate
                          </Text>
                        </Col>
                      </Row>
                    </Col>
                    <Col flex="0 1 auto">
                      <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }} ellipsis={{ tooltip: true }}>
                        {purchaseRequest.client?.conversionRate
                          ? `${purchaseRequest.client?.conversionRate}%`
                          : 'Not Available'}
                      </Text>
                    </Col>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row align={'middle'} justify={'space-between'} wrap={false} gutter={4}>
                    <Col>
                      <Row align={'middle'} className={'gap-4'} wrap={false}>
                        <Col>
                          <DollarSignIcon />
                        </Col>
                        <Col>
                          <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                            Total Amount
                          </Text>
                        </Col>
                      </Row>
                    </Col>
                    <Col flex="0 1 auto">
                      <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }} ellipsis={{ tooltip: true }}>
                        {purchaseRequest.client?.totalAmount
                          ? `${purchaseRequest.client?.totalAmount}€`
                          : 'Not Available'}
                      </Text>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={6}>
                {purchaseRequest.client?.manualTags?.map((_tag: string, index: number) => (
                  <Col key={index + _tag}>
                    <Tag title={_tag} className={'custom-tag'}>
                      {_tag}
                    </Tag>
                  </Col>
                ))}
              </Row>
            </Col>
            <Col span={24}>
              <MultiLineEllipsisText
                maxLines={3}
                subTitle={'Additional Notes:'}
                text={purchaseRequest?.client?.additionalNotes}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <AssignmentEditModal open={assignmentModalState} onCancel={onAssignmentCancel} />
    </>
  );
});
